import {
  Badge,
  Box,
  Button,
  Card,
  Flex,
  Grid,
  Heading,
  HStack,
  Text,
  Textarea,
  VStack,
} from "components/ui";
import { useTranslation } from "hooks/useTranslation";
import { useEffect, useMemo, useState } from "react";
import {
  MdArrowBack,
  MdAttachFile,
  MdBuild,
  MdEmail,
  MdMessage,
  MdSmartToy,
} from "react-icons/md";
import { useNavigate, useParams } from "react-router-dom";
import { NOTIFICATION_TOOL_NAMES } from "constants/builtInTools";
import {
  CustomerChatTimeline,
  CustomerChatTimelineItem,
  customerService,
} from "services/customerService";
import { formatFileSize } from "utils/formatFileSize";
import LogDetails from "views/admin/logs/details";
import ToolExecutionLogDetails from "views/admin/tool-execution-logs/details";

const CustomerChatDetails = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { customerId, chatId } = useParams<{
    customerId: string;
    chatId: string;
  }>();

  const [timeline, setTimeline] = useState<CustomerChatTimeline | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedItem, setSelectedItem] =
    useState<CustomerChatTimelineItem | null>(null);

  const loadTimeline = async () => {
    if (!customerId || !chatId) return;

    try {
      setLoading(true);
      const data = await customerService.getCustomerChatTimeline(
        customerId,
        chatId,
      );
      setTimeline(data);
      setError("");
    } catch (err: any) {
      setError(
        err.response?.data?.error ||
          t("app.customers.details.chatTimeline.loadFailed"),
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTimeline();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId, chatId]);

  const handleSelectItem = async (item: CustomerChatTimelineItem) => {
    setSelectedItem(item);
  };

  const selectedMessageContent = useMemo(() => {
    if (!selectedItem || selectedItem.itemType !== "message") return "";
    return selectedItem.preview || "";
  }, [selectedItem]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  if (loading) {
    return (
      <Card.Root>
        <Card.Body>
          <Text textAlign="center">
            {t("app.customers.details.chatTimeline.loading")}
          </Text>
        </Card.Body>
      </Card.Root>
    );
  }

  if (!timeline) {
    return (
      <Card.Root>
        <Card.Body>
          <Button
            variant="ghost"
            size="sm"
            mb={3}
            onClick={() => navigate(`/admin/customers/${customerId}`)}
          >
            <MdArrowBack />
            {t("app.common.back")}
          </Button>
          <Text textAlign="center" color="red.600">
            {t("app.customers.details.chatTimeline.notFound")}
          </Text>
        </Card.Body>
      </Card.Root>
    );
  }

  return (
    <Flex direction="column" gap={5} w="full">
      <Flex justify="space-between" align="center">
        <HStack>
          <Button
            variant="ghost"
            onClick={() => navigate(`/admin/customers/${customerId}`)}
            size="sm"
          >
            <MdArrowBack />
          </Button>
          <Heading size="2xl" fontWeight="bold">
            {t("app.customers.details.chatTimeline.title")}
          </Heading>
        </HStack>
      </Flex>

      {error && (
        <Box borderRadius="lg" bg="red.100" p={3}>
          <Text fontSize="sm" color="red.700">
            {error}
          </Text>
        </Box>
      )}

      <Grid
        templateColumns={{ base: "1fr", xl: "1.3fr 1fr" }}
        gap={4}
        alignItems="start"
      >
        <Card.Root>
          <Card.Body p={0}>
            <Box
              p={4}
              borderBottomWidth="1px"
              borderColor={{ base: "gray.200", _dark: "gray.700" }}
            >
              <Heading size="md">
                {timeline.title ||
                  t("app.customers.details.chatHistory.unnamed")}
              </Heading>
              <Text fontSize="sm" color="gray.500">
                {t("app.customers.details.chatTimeline.totalItems", {
                  count: timeline.items.length,
                })}
              </Text>
            </Box>

            <Box maxH="70vh" overflowY="auto">
              {timeline.items.length === 0 ? (
                <Box p={4}>
                  <Text fontSize="sm" color="gray.500">
                    {t("app.customers.details.chatTimeline.empty")}
                  </Text>
                </Box>
              ) : (
                timeline.items.map((item) => (
                  <Box
                    key={`${item.itemType}-${item.itemId}`}
                    p={4}
                    borderBottomWidth="1px"
                    borderColor={{ base: "gray.200", _dark: "gray.700" }}
                    cursor="pointer"
                    bg={
                      selectedItem?.itemId === item.itemId &&
                      selectedItem?.itemType === item.itemType
                        ? { base: "blue.50", _dark: "whiteAlpha.100" }
                        : undefined
                    }
                    onClick={() => handleSelectItem(item)}
                  >
                    <Flex justify="space-between" align="center" mb={1}>
                      <HStack>
                        {item.itemType === "message" && <MdMessage />}
                        {item.itemType === "ai_execution" && <MdSmartToy />}
                        {item.itemType === "tool_execution" && <MdBuild />}
                        <Text fontWeight="medium" fontSize="sm">
                          {item.title}
                        </Text>
                        {item.itemType === "tool_execution" &&
                          NOTIFICATION_TOOL_NAMES.has(item.title) && (
                            <Badge
                              colorPalette="teal"
                              variant="subtle"
                              size="sm"
                            >
                              <HStack gap={1}>
                                <MdEmail size={10} />
                                <span>
                                  {t("app.logs.details.notification")}
                                </span>
                              </HStack>
                            </Badge>
                          )}
                        {item.itemType === "ai_execution" &&
                          item.hasFeedback && (
                            <Badge
                              colorPalette={
                                item.feedbackVote === "Positive"
                                  ? "green"
                                  : "red"
                              }
                              variant="subtle"
                            >
                              {item.feedbackVote === "Positive" ? "👍" : "👎"}
                            </Badge>
                          )}
                        {item.itemType !== "message" &&
                          typeof item.isSuccess === "boolean" && (
                            <Badge
                              colorPalette={item.isSuccess ? "green" : "red"}
                              variant="subtle"
                            >
                              {item.isSuccess
                                ? t("app.common.success")
                                : t("app.common.failed")}
                            </Badge>
                          )}
                      </HStack>
                      <Text fontSize="xs" color="gray.500">
                        {formatDate(item.timestamp)}
                      </Text>
                    </Flex>

                    {item.preview && (
                      <Text
                        fontSize="sm"
                        color={{ base: "gray.900", _dark: "gray.100" }}
                        lineClamp={2}
                      >
                        {item.preview}
                      </Text>
                    )}

                    {/* Files the customer attached to this message — named here so an
                        admin reading the transcript sees the message was not text-only. */}
                    {item.attachments && item.attachments.length > 0 && (
                      <Flex wrap="wrap" gap={1} mt={2}>
                        {item.attachments.map((attachment) => (
                          <Badge
                            key={attachment.id}
                            colorPalette="purple"
                            variant="subtle"
                            size="sm"
                          >
                            <HStack gap={1}>
                              <MdAttachFile size={10} />
                              <span>
                                {attachment.fileName ||
                                  t(
                                    "app.customers.details.chatTimeline.unnamedAttachment",
                                  )}
                              </span>
                            </HStack>
                          </Badge>
                        ))}
                      </Flex>
                    )}
                  </Box>
                ))
              )}
            </Box>
          </Card.Body>
        </Card.Root>

        <Card.Root>
          <Card.Body p={4}>
            <Heading size="md" mb={3}>
              {t("app.customers.details.chatTimeline.details")}
            </Heading>

            <Box maxH="70vh" overflowY="auto" pr={1}>
              {!selectedItem && (
                <Text fontSize="sm" color="gray.500">
                  {t("app.customers.details.chatTimeline.selectItem")}
                </Text>
              )}

              {selectedItem && selectedItem.itemType === "message" && (
                <Box>
                  <Text fontSize="xs" color="gray.500" mb={1}>
                    {t("app.customers.details.chatTimeline.messageRole")}
                  </Text>
                  <Text fontSize="sm" mb={3}>
                    {selectedItem.messageRole || t("app.common.notAvailable")}
                  </Text>
                  <Textarea
                    value={selectedMessageContent}
                    readOnly
                    rows={16}
                    fontFamily="mono"
                    fontSize="sm"
                  />

                  {selectedItem.attachments &&
                    selectedItem.attachments.length > 0 && (
                      <Box mt={4}>
                        <Text fontSize="xs" color="gray.500" mb={2}>
                          {t("app.customers.details.chatTimeline.attachments")}
                        </Text>
                        <VStack align="stretch" gap={2}>
                          {selectedItem.attachments.map((attachment) => (
                            <Flex
                              key={attachment.id}
                              align="center"
                              gap={2}
                              p={2}
                              borderWidth="1px"
                              borderRadius="md"
                              borderColor={{
                                base: "gray.200",
                                _dark: "gray.700",
                              }}
                            >
                              <MdAttachFile size={14} />
                              <Text fontSize="sm" fontWeight="medium">
                                {attachment.fileName ||
                                  t(
                                    "app.customers.details.chatTimeline.unnamedAttachment",
                                  )}
                              </Text>
                              <Text fontSize="xs" color="gray.500">
                                {attachment.mimeType}
                                {attachment.fileSizeBytes > 0 &&
                                  ` · ${formatFileSize(attachment.fileSizeBytes)}`}
                              </Text>
                            </Flex>
                          ))}
                        </VStack>
                      </Box>
                    )}
                </Box>
              )}

              {selectedItem && selectedItem.itemType === "ai_execution" && (
                <LogDetails logId={selectedItem.itemId} embedded />
              )}

              {selectedItem && selectedItem.itemType === "tool_execution" && (
                <ToolExecutionLogDetails logId={selectedItem.itemId} embedded />
              )}
            </Box>
          </Card.Body>
        </Card.Root>
      </Grid>
    </Flex>
  );
};

export default CustomerChatDetails;
